import { Component, Input, forwardRef, OnInit, ElementRef, Output, EventEmitter, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectorStatus } from '../items-selector/items-selector.component';
import { ItemNode } from '../models/item';
import { SelectedItemsVisualizerOverlayService } from '../selected-items-visualizer-overlay/selected-items-visualizer-overlay.service';
import { Observable, Subscriber } from 'rxjs';
import { SelectedItemsVisualizerOverlayRef } from '../selected-items-visualizer-overlay/selected-items-visualizer-overlay-ref';

@Component({
    // eslint-disable-next-line @angular-eslint/component-selector
    selector: 'se-selector-web',
    templateUrl: './se-selector-web.component.html',
    styleUrls: ['./se-selector-web.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SeSelectorWebComponent),
            multi: true
        }]
})
export class SeSelectorWebComponent implements ControlValueAccessor, OnChanges, OnDestroy, OnInit {

    private _itemList: ItemNode[];
    get itemList(): ItemNode[] {
        return this._itemList;
    }
    @Input() set itemList(value: ItemNode[]) {
        //la collection la normalizzo qui
        this._itemList = value?.map(v => new ItemNode(v));
    }

    @Input() emitItemsById: boolean;
    @Input() visibleSelectAllButton: boolean;
    @Input() title: string;
    @Input() noItemsSelectedMessage: string;
    @Input() inputSearchText: string;
    @Input() status: SelectorStatus = 'view';
    @Input() filter: string;
    @Input() isInError = false;
    @Input() autoSelectParent: boolean = false;

    @Output() filterChange = new EventEmitter<string>();
    @Output() maxSelectableItemsNotify = new EventEmitter<void>();
    @Output() notSelectableItemsNotify = new EventEmitter<void>();
    @Output() statusChange = new EventEmitter<SelectorStatus>();
    @Output() isEmpty = new EventEmitter<boolean>();

    disabled : boolean;
    focusElement : boolean = false;
    itemListFlat: ItemNode[];
    loading = false;
    visualizerPopupSelectedItemsChangeSubscriber: Subscriber<ItemNode[]>;
    selectedItemsVisOverlayRef: SelectedItemsVisualizerOverlayRef;
    selectedItems: ItemNode[];
    selectedItemsChangeListener: Function = () => {
        // inizializzata a funzione vuota per prevenire l'errore this.selectedItemsChangeListener is not a function
    };

    constructor(private selectedItemsVisOverlay: SelectedItemsVisualizerOverlayService, private element: ElementRef) { }

    ngOnInit() {
        this.buildItemListFlat();
    }

    ngOnChanges({ status }: SimpleChanges) {
        if (status) {
            this.toggleVisualizer();
        }
    }

    ngOnDestroy() {
        if (this.selectedItemsVisOverlayRef) {
            this.selectedItemsVisOverlayRef.close();
        }
        if (this.visualizerPopupSelectedItemsChangeSubscriber) {
            this.visualizerPopupSelectedItemsChangeSubscriber.unsubscribe();
        }
    }

    maxSelectableItems() {
        this.maxSelectableItemsNotify.emit();
    }

    notSelectableItems() {
        this.notSelectableItemsNotify.emit();
    }

    private buildItemListFlat() {
        if (this.itemList) {
            const itemListFlatRes = [];
            this.itemList.forEach(node => {
                this.addNodeToFlatListRecursive(node, itemListFlatRes);
            });
            this.itemListFlat = itemListFlatRes;
        }
    }

    private addNodeToFlatListRecursive(node: ItemNode, itemListFlatRes: ItemNode[]) {
        itemListFlatRes.push(node);
        if (node.children) {
            node.children.forEach(child => {
                this.addNodeToFlatListRecursive(child, itemListFlatRes);
            });
        }
    }

    private openSelectedItemOverlay() {
        // observable used by the visualizer to receive updates about selected components in the selector
        const selectedItemsObservable = new Observable<ItemNode[]>(subscriber => {
            this.visualizerPopupSelectedItemsChangeSubscriber = subscriber;
            if (this.selectedItems) {
                subscriber.next(this.selectedItems);
            }
        });
        this.selectedItemsVisOverlayRef = this.selectedItemsVisOverlay.open(this.element, selectedItemsObservable, this.itemList);
    }

    public changeStatus(focus) {
        const status = (focus) ? 'edit' : 'view';

        const itsChanged = (status !== this.status);
        if (itsChanged) {
            this.status = status;
            this.statusChange.emit(status);
            if (status === 'view') {
                this.inputSearchText = '';
                this.filterChange.emit('');
            }

            this.toggleVisualizer();
        }
    }

    onIsEmptyChange(isEmpty: boolean) {
        this.isEmpty.emit(isEmpty);
    }

    private toggleVisualizer() {
        switch (this.status) {
            case 'view':
                if (this.selectedItemsVisOverlayRef) {
                    this.selectedItemsVisOverlayRef.close();
                    this.selectedItemsVisOverlayRef = null;
                }
                break;
            case 'edit':
                if (!this.selectedItemsVisOverlayRef) {
                    this.openSelectedItemOverlay();
                }
                break;
        }
    }


    /* Control Value Accessor Methods */
    writeValue(selectedItemsRaw: any) {
        if (selectedItemsRaw && selectedItemsRaw !== this.selectedItems) {
            this.selectedItems = this.buildItemList(selectedItemsRaw);
            if (this.visualizerPopupSelectedItemsChangeSubscriber) {
                this.visualizerPopupSelectedItemsChangeSubscriber.next(this.selectedItems);
            }
        }
    }

    onItemSelectorSelectedItemsChange(items) {
        this.selectedItems = items;
        this.emitSelectedItemsChangeEvent(items);
        if (this.visualizerPopupSelectedItemsChangeSubscriber) {
            this.visualizerPopupSelectedItemsChangeSubscriber.next(items);
        }
    }

    onLoading(loading: boolean) {
        this.loading = loading;
    }

    private emitSelectedItemsChangeEvent(items: any) {
        items.sort((a, b) => a.Order - b.Order);
        if (this.emitItemsById) {
            this.selectedItemsChangeListener(items.map(s => s.Id));
        } else {
            this.selectedItemsChangeListener(items);
        }
    }

    private buildItemList(selectedItemsRaw: any) {
        let result: ItemNode[] = [];
        if (typeof selectedItemsRaw[0] === 'object') { // in input si ricevono direttamente gli item node
            result = selectedItemsRaw;
        } else if (this.itemListFlat) { // dagli id recupero gli item node
            result = this.itemListFlat.filter(x => (selectedItemsRaw.indexOf(x.Id) > -1));
        }
        return result;
    }

    registerOnChange(changeListenerFn: any) {
        this.selectedItemsChangeListener = changeListenerFn;
    }

    registerOnTouched(_: any) {
        /* chiedere a ebit se implementare */
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    focus () {
        this.focusElement = true;
    }

    focusOut () {
        this.focusElement = false;
    }
}
